import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Parallax, Background } from 'react-parallax';
import InnotecIsotipo from "../assets/Icono_isotipo.svg";
import InnotecLogo from "../assets/logo-color-blanco.svg";
import timelineInicio from "../assets/historia/timeline-inicio.png";
import timelineExpansion from "../assets/historia/timeline-expansion.jpg";

// Imágenes (ajusta las rutas según tus assets reales)
const backgroundHero = '/images/nosotros/hero-background.jpg';
const timelineImages = {
  inicio: '/images/nosotros/timeline-inicio.png',
  crecimiento: '/images/nosotros/timeline-inicio.png',
  expansion: '/images/nosotros/timeline-expansion.jpg',
  actualidad: '/images/nosotros/timeline-expansion.jpg',
};

const Nosotros = () => {
  // Estado para el submenú y seguimiento de sección visible
  const [activeSection, setActiveSection] = useState('historia');

  // Efecto para actualizar la sección activa al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentActiveSection = 'historia';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top + window.scrollY;
        const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop - 200 && window.scrollY < sectionTop + sectionHeight - 200) {
          const sectionId = section.getAttribute('id');
          if (sectionId) {
            currentActiveSection = sectionId;
          }
        }
      });

      setActiveSection(currentActiveSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Variantes de animación
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  // Para el efecto de blur al hacer scroll
  const { scrollYProgress } = useScroll();
  const blurValue = useTransform(scrollYProgress, [0, 0.1], [0, 6]);

  // Secciones del submenú
  const sections = [
    { id: 'historia', label: 'Historia' },
    { id: 'valores', label: 'Valores' },
    { id: 'trayectoria', label: 'Trayectoria' },
    { id: 'equipo', label: 'Equipo' },
    { id: 'transporte', label: 'Transporte' },
  ];

  // Función para scroll suave a secciones
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
    setActiveSection(sectionId);
  };

  return (
    <div className="bg-[#0A0A0B] min-h-screen">
      {/* Submenú de Navegación (con movimiento suave y efecto de vidrio mejorado) */}
      <nav className="fixed top-32 left-1/2 transform -translate-x-1/2 z-40 bg-[#0A0A0B]/80 backdrop-blur-md rounded-full px-6 py-3 shadow-xl border border-white/10 transition-all duration-300 mt-8">
        <ul className="flex space-x-6 md:space-x-10">
          {sections.map((section) => (
            <li key={section.id}>
              <button
                className={`text-sm font-medium transition-all duration-300 relative ${
                  activeSection === section.id
                    ? 'text-[#FCAA0D]'
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.label}
                {activeSection === section.id && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#FCAA0D]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Hero Section con Parallax y efecto de video */}
      <section id="historia" className="relative">
        <Parallax bgImage={backgroundHero} strength={400} blur={{ min: -10, max: 15 }}>
          <div className="h-screen flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/40 via-transparent to-[#0A0A0B] z-10" />

            {/* Overlay de textura para dar profundidad */}
            <div className="absolute inset-0 bg-[url('/images/nosotros/noise-texture.png')] opacity-5 mix-blend-overlay z-10" />

            {/* Logo de Innotec centrado sin efectos */}
            <div
              className="absolute top-1/4 left-1/2 transform -translate-x-1/2 z-20"
              style={{ width: '40%', maxWidth: '500px' }}
            >
              <img
                src={InnotecLogo}
                alt="Innotec Logo"
                className="w-full h-auto"
              />
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center max-w-4xl mx-auto px-4 z-20"
            >
              <span className="inline-block py-1 px-4 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium mb-6">
                Desde 1985
              </span>
              <h1 className="text-5xl md:text-7xl font-extrabold mb-6 text-white mt-32">
                Sobre <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FCAA0D] to-[#F7931E]">nosotros</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
                Líderes mundiales en maquinaria para la industria alimentaria, combinando tradición, innovación y excelencia.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-[#FCAA0D] hover:bg-[#F7931E] text-black font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#FCAA0D]/20">
                  Explora nuestra historia
                </button>
                <button className="bg-transparent hover:bg-white/10 text-white border border-white/20 font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105">
                  Contactar
                </button>
              </div>
            </motion.div>
          </div>
        </Parallax>
      </section>

      {/* Valores Section - Diseño con iconos y animación más fluida */}
      <section id="valores" className="py-24 relative">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#0A0A0B] to-transparent z-10" />

        {/* Isotipo como elemento decorativo estático */}
        <div
          className="absolute right-0 top-1/4 opacity-10 z-0"
          style={{ width: '40%', maxWidth: '400px' }}
        >
          <img src={InnotecIsotipo} alt="Innotec Isotipo" className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="inline-block py-1 px-4 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium mb-4">
              Lo que nos mueve
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Nuestros Valores
            </h2>
            <div className="mt-4 w-24 h-1 bg-gradient-to-r from-[#FCAA0D] to-[#F7931E] mx-auto rounded-full" />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: '🔍',
                title: 'Innovación',
                desc: 'Utilizamos las últimas tecnologías para cumplir con los estándares europeos de seguridad e higiene.'
              },
              {
                icon: '🏆',
                title: 'Excelencia',
                desc: 'Más de 35 años de experiencia nos avalan como líderes en el sector alimentario industrial.'
              },
              {
                icon: '🤝',
                title: 'Compromiso',
                desc: 'Ofrecemos maquinaria competitiva con un rendimiento óptimo y soporte continuo.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gradient-to-br from-white/5 to-white/[0.02] p-8 rounded-xl border border-white/10 hover:border-[#FCAA0D]/50 transition-all duration-500 group hover:shadow-lg hover:shadow-[#FCAA0D]/5"
              >
                <div className="w-16 h-16 bg-[#FCAA0D]/10 rounded-full flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#FCAA0D] group-hover:text-[#F7931E] transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-300 group-hover:text-white transition-colors duration-300">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline Section - Reimaginada con efectos visuales mejorados */}
      <section id="trayectoria" className="py-24 relative">
        <div className="absolute inset-0 bg-[url('/images/nosotros/grid-pattern.svg')] opacity-5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="inline-block py-1 px-4 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium mb-4">
              35+ años de historia
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
              Nuestra Trayectoria
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Desde un pequeño taller hasta convertirnos en líderes internacionales
            </p>
            <div className="mt-4 w-24 h-1 bg-gradient-to-r from-[#FCAA0D] to-[#F7931E] mx-auto rounded-full" />
          </motion.div>

          <div className="relative">
            {/* Línea central con efecto de brillo */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-[#FCAA0D]/80 via-[#FCAA0D]/50 to-[#FCAA0D]/20 rounded-full" />

            {/* Efecto de luz en la línea */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-12 bg-gradient-to-b from-[#FCAA0D]/30 via-[#FCAA0D]/10 to-transparent blur-xl" />

            {[
              { year: '1985', title: 'Los Inicios', desc: 'Un pequeño taller mecánico especializado en la industria cárnica que sentó las bases de nuestra filosofía de trabajo.', img: timelineImages.inicio },
              { year: '1995', title: 'Crecimiento', desc: 'Expansión de instalaciones y oferta de productos para satisfacer la creciente demanda nacional.', img: timelineImages.crecimiento },
              { year: '2005', title: 'Expansión Internacional', desc: 'Primera exportación a Portugal y Francia, marcando el inicio de nuestra presencia en la Unión Europea.', img: timelineImages.expansion },
              { year: '2025', title: 'Actualidad', desc: 'Líderes con presencia global, 35+ años de experiencia y un catálogo que abarca toda la cadena alimentaria.', img: timelineImages.actualidad },
            ].map((item, index) => (
              <motion.div
                key={item.year}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
                className={`relative mb-24 md:mb-32 flex flex-col ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } items-center`}
              >
                <div className={`w-full md:w-1/2 px-6 text-center md:text-${index % 2 === 0 ? 'right' : 'left'} mb-8 md:mb-0`}>
                  <span className="inline-block py-1 px-3 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium mb-2">
                    {item.year}
                  </span>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-300">{item.desc}</p>
                </div>

                {/* Punto brillante en la línea de tiempo */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full bg-[#FCAA0D] border-4 border-[#0A0A0B] shadow-lg shadow-[#FCAA0D]/50 z-10" />

                <div className="w-full md:w-1/2 px-6">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden rounded-xl shadow-lg shadow-black/30 border border-white/10 group"
                  >
                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent opacity-60" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipo Section - Rediseñado con tarjetas más atractivas */}
      <section id="equipo" className="py-24 relative bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="inline-block py-1 px-4 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium mb-4">
              Nuestro motor
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
              El Equipo INNOTEC
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Profesionales apasionados por la industria alimentaria
            </p>
            <div className="mt-4 w-24 h-1 bg-gradient-to-r from-[#FCAA0D] to-[#F7931E] mx-auto rounded-full" />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: '🔧',
                title: 'Departamento Técnico',
                desc: 'Expertos en maquinaria industrial alimentaria con formación continua.',
                stats: '+25 profesionales'
              },
              {
                icon: '💼',
                title: 'Departamento Comercial',
                desc: 'Asesores especializados para encontrar la solución perfecta para tu negocio.',
                stats: 'Presentes en 15 países'
              },
              {
                icon: '🛠️',
                title: 'Servicio Post-venta',
                desc: 'Soporte técnico de excelencia disponible 24/7 para nuestros clientes.',
                stats: '98% satisfacción'
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gradient-to-br from-white/8 to-transparent p-8 rounded-xl border border-white/10 hover:border-[#FCAA0D]/50 transition-all duration-500 group hover:shadow-lg hover:shadow-[#FCAA0D]/5 relative overflow-hidden"
              >
                {/* Efecto de brillo en la esquina */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#FCAA0D]/10 rounded-full blur-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700" />

                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-[#FCAA0D]/20 rounded-full flex items-center justify-center mr-4 text-2xl">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-[#FCAA0D] transition-colors duration-300">
                    {item.title}
                  </h3>
                </div>

                <p className="text-gray-300 mb-6 relative z-10">
                  {item.desc}
                </p>

                <div className="pt-4 border-t border-white/10">
                  <span className="text-[#FCAA0D] font-semibold">{item.stats}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Transporte Section - Mejorado con más elementos visuales */}
      <section id="transporte" className="py-24 relative">
        <Parallax
          bgImage="/images/nosotros/transporte-bg.jpg"
          strength={400}
          renderLayer={percentage => (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `rgba(10, 10, 11, ${0.5 - percentage * 0.3})`,
                backgroundImage: `linear-gradient(to bottom, rgba(10, 10, 11, 0.7), rgba(252, 170, 13, ${0.2 * percentage}))`
              }}
            />
          )}
        >
          <div className="min-h-[80vh] flex items-center justify-center relative">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="max-w-4xl mx-auto px-4 text-center z-10 py-16"
            >
              <span className="inline-block py-1 px-4 rounded-full bg-[#FCAA0D]/30 text-[#FCAA0D] text-sm font-medium mb-6">
                Logística especializada
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Transporte y Entrega
              </h2>
              <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
                En INNOTEC, nuestras máquinas viajan en primera clase. Con un embalaje riguroso y seguros que superan su valor, garantizamos una entrega impecable en cualquier parte del mundo.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {[
                  { title: 'Embalaje Certificado', desc: 'Protección total para la maquinaria' },
                  { title: 'Transporte Especializado', desc: 'Vehículos adaptados a cada tipo de carga' },
                  { title: 'Seguimiento en Tiempo Real', desc: 'Monitorización constante de la entrega' },
                ].map((item, index) => (
                  <div key={index} className="bg-black/40 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                    <h3 className="text-lg font-bold mb-2 text-[#FCAA0D]">{item.title}</h3>
                    <p className="text-gray-300 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="bg-[#FCAA0D] hover:bg-[#F7931E] text-black font-bold py-3 px-10 rounded-full transition-all duration-300 shadow-lg shadow-[#FCAA0D]/20"
              >
                Solicitar información
              </motion.button>
            </motion.div>
          </div>
        </Parallax>
      </section>

      {/* CTA Section - Rediseñado con un formulario simple */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B] via-[#0A0A0B] to-[#FCAA0D]/10" />

        {/* Elementos decorativos */}
        <div className="absolute left-0 top-1/4 w-64 h-64 bg-[#FCAA0D]/20 rounded-full blur-3xl opacity-30" />
        <div className="absolute right-0 bottom-1/4 w-96 h-96 bg-[#FCAA0D]/10 rounded-full blur-3xl opacity-20" />

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="max-w-6xl mx-auto px-4 relative z-10"
        >
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-8 md:p-12 border border-white/10 shadow-xl">
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-1/2 md:pr-12 mb-8 md:mb-0">
                <span className="inline-block py-1 px-4 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium mb-6">
                  Únete a INNOTEC
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  Lleva tu negocio al siguiente nivel
                </h2>
                <p className="text-gray-300 mb-8">
                  Mejora tu producción con maquinaria de alta calidad y un servicio inigualable. Nuestros expertos están listos para asesorarte.
                </p>

                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gray-600 border-2 border-[#0A0A0B]" />
                    ))}
                  </div>
                  <div>
                    <p className="text-white font-medium">+500 clientes satisfechos</p>
                    <div className="flex text-yellow-500">★★★★★</div>
                  </div>
                </div>
              </div>

              <div className="w-full md:w-1/2 bg-black/30 rounded-xl p-8 backdrop-blur-sm border border-white/10">
                <h3 className="text-xl font-bold mb-6 text-white">Contáctanos ahora</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Nombre</label>
                    <input
                      type="text"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FCAA0D] focus:outline-none transition-colors duration-300"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                    <input
                      type="email"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FCAA0D] focus:outline-none transition-colors duration-300"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Mensaje</label>
                    <textarea
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FCAA0D] focus:outline-none transition-colors duration-300 h-24"
                      placeholder="¿En qué podemos ayudarte?"
                    ></textarea>
                  </div>

                  <button className="w-full bg-[#FCAA0D] hover:bg-[#F7931E] text-black font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg">
                    Enviar mensaje
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Nosotros;
